import React, { useState, useEffect } from "react";
import RAccordionLoader from "../../States/Loading/Loaders/shared/RAccordionLoader";
import { View, StyleSheet, Platform } from "react-native";
import { useResponsive } from "../../../hooks/useResponsive";
import RText from "../../RText";
import RBettingTile from "./RBettingTile";
import variables from "../../../styles/variables";
import { BettingTile } from "../../../types/bettingTile";
import { APIResponse } from "../../../types/responses/APIResponse";
import { fetchGet } from "../../../client";
import useDeviceType from "../../../hooks/useDeviceType";

/**
 * RBettingTileList component fetches and displays a list of betting tiles
 *
 * It is one of the RAttachment's that can be added to page content in the CMS, but can
 * also be displayed on thew news article page
 *
 * @param {string} id - The ID of the betting tile list to fetch.
 * @param {BettingTile[]} dataOverride - Optional override data for the betting tiles.
 * @param {boolean} isAttachment - Whether the component is being used as an attachment.
 * @param {boolean} isRelatedContent - Whether the tiles are being displayed as related content on the news page.
 *
 * @returns {JSX.Element | null} The rendered RBettingTileList component or null if loading/error.
 */

const RBettingTileList = ({
  id,
  dataOverride,
  isAttachment,
  isRelatedContent,
}: {
  id?: string;
  dataOverride?: BettingTile[];
  isAttachment?: boolean;
  isRelatedContent?: boolean;
}) => {
  const [data, setData] = useState(dataOverride || []);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  const { deviceType } = useDeviceType();
  const isTabletNative = deviceType === "TABLET" && Platform.OS !== "web";

  useEffect(() => {
    if (dataOverride) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const bettingTilesRes: APIResponse<{ list: BettingTile[] }> =
          await fetchGet(`betting-tiles/lists/${id}`);
        setData(bettingTilesRes?.list ?? []);
      } catch (err) {
        setError("Error loading betting tiles:" + err?.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <RAccordionLoader />;
  if (error || !data.length) return null;

  return (
    <View style={{ width: "100%" }}>
      <Header isAttachment={isAttachment && !isTabletNative} />
      <View style={{ marginTop: 10 }}>
        {data.map((bettingTile: BettingTile, index: number) => {
          //don't display top offer if in related content as it will be shown
          //above this component
          if (index > 6 || (isRelatedContent && bettingTile?.top_offer))
            return null;
          return (
            <RBettingTile
              key={bettingTile.id}
              id={bettingTile.id}
              data={bettingTile}
              isGrouped
              hasData
              isRelatedContent={isRelatedContent}
            />
          );
        })}
      </View>
    </View>
  );
};

const Header = ({ isAttachment }: { isAttachment?: boolean }) => {
  const { isLargeAndUp } = useResponsive();

  return (
    <View
      style={[
        styles.relatedPostsHeader,
        { marginTop: isAttachment ? 0 : spacing.xsmall },
      ]}
    >
      <RText
        uppercase
        weight="bold"
        style={{ paddingHorizontal: 8, paddingVertical: 5 }}
        color="primary"
        family="secondary"
        size={isLargeAndUp ? "md_lg" : "md"}
      >
        Featured offers
      </RText>
    </View>
  );
};

const { colors, spacing } = variables;

const styles = StyleSheet.create({
  relatedPostsHeader: {
    backgroundColor: colors.palette.neutral.x100,
  },
  post: {
    paddingVertical: 15,
    borderBottomColor: colors.palette.neutral.x100,
    borderBottomWidth: 1,
    borderStyle: "solid",
  },
});

export default RBettingTileList;
